/**
 * Dr Moto — Parts Finder hero
 *
 * The Seeker parts finder renders its results inline, directly beneath its own
 * filter row. Once a bike is chosen the page is effectively a parts listing, so
 * the hero collapses to a compact filter bar instead of towering over them.
 *
 * Whether results are showing is read from the finder's own DOM — it renders an
 * <article class="parts"> only when it has some — rather than from the query
 * string. The app rewrites the URL with history.replaceState, including
 * clearing year/make/model when a stale or unresolvable link is opened, and
 * those rewrites fire no event. A URL-based check therefore strands the hero
 * collapsed above an empty finder.
 *
 * The finder's markup lives in an open shadow root, so the observer has to be
 * pointed at that root; observing this element's own light DOM never sees a
 * single mutation.
 *
 * Progressive enhancement: with JS off the hero simply stays expanded.
 */
const RESULTS_SELECTOR = 'article.parts, .category';
// The app embed hydrates late on a cold load — measured past 20s — so give the
// poll plenty of room. Missing the window leaves the hero stuck on the URL guess.
const ATTACH_TIMEOUT_MS = 60000;
const ATTACH_POLL_MS = 200;

class PartsFinderHero extends HTMLElement {
  connectedCallback() {
    if (this.dataset.compactOnResults !== 'true') return;

    this.sync = this.sync.bind(this);
    this.hero = this.closest('.pf-hero');

    window.addEventListener('popstate', this.sync);
    // First guess from the URL so a genuine results link does not flash the
    // full-height hero before collapsing.
    this.sync();
    this.attachToFinder();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.sync);
    this.observer?.disconnect();
    clearInterval(this.pollId);
    cancelAnimationFrame(this.frameId);
  }

  /* The section renders <seeker-dropdown-parts-finder-root>; the app then nests
     its own <seeker-dropdown-parts-finder> inside it, and the shadow root hangs
     off that inner element, not the one in the template. Hydration can take
     20s+ on a cold load. The descendant scan is a safety net in case the app
     renames or re-nests its elements. */
  get finderRoot() {
    const named = this.querySelector('seeker-dropdown-parts-finder');
    if (named?.shadowRoot) return named.shadowRoot;

    for (const el of this.querySelectorAll('*')) {
      if (el.shadowRoot) return el.shadowRoot;
    }
    return null;
  }

  /** The app upgrades its element asynchronously, so wait for the shadow root. */
  attachToFinder() {
    const attach = () => {
      const root = this.finderRoot;
      if (!root) return false;
      this.observer = new MutationObserver(() => this.scheduleSync());
      this.observer.observe(root, { childList: true, subtree: true });
      this.sync();
      return true;
    };

    if (attach()) return;

    const deadline = Date.now() + ATTACH_TIMEOUT_MS;
    this.pollId = setInterval(() => {
      if (attach() || Date.now() > deadline) clearInterval(this.pollId);
    }, ATTACH_POLL_MS);
  }

  /* Rendering a result set fires hundreds of mutations; collapse them into one
     check per frame. */
  scheduleSync() {
    cancelAnimationFrame(this.frameId);
    this.frameId = requestAnimationFrame(this.sync);
  }

  get hasResults() {
    const root = this.finderRoot;
    if (root) return Boolean(root.querySelector(RESULTS_SELECTOR));

    const model = new URLSearchParams(window.location.search).get('model');
    return Boolean(model && model.trim());
  }

  sync() {
    this.hero?.classList.toggle('pf-hero--results', this.hasResults);
  }
}

if (!customElements.get('parts-finder-hero')) {
  customElements.define('parts-finder-hero', PartsFinderHero);
}
